/* One serialized writer owns every cart view. Responses, not estimated prices, finalize totals. */
(() => {
  "use strict";
  const A = window.Avenger;
  if (!A) return;
  class CartService extends EventTarget {
    constructor() {
      super();
      this.tail = Promise.resolve();
      this.pending = 0;
      this.intents = new Map();
      this.quantityTimers = new Map();
      this.noteTimer = null;
      this.state = JSON.parse(
        document.querySelector("[data-cart-state]")?.textContent ||
          '{"items":[],"item_count":0}',
      );
      this.version = 0;
      this.writeVersion = 0;
      this.codes = [];
      try {
        this.codes = JSON.parse(
          sessionStorage.getItem("avenger-cart-codes") || "[]",
        )
          .filter((c) => typeof c === "string")
          .slice(0, 20);
      } catch (_) {}
      this.refresh().catch(() => {});
      document.addEventListener("av:page", () =>
        this.refresh().catch((error) => this.error(error)),
      );
    }
    enqueue(task) {
      this.lastError = null;
      A.invalidate?.();
      document.querySelectorAll("[data-cart-error]").forEach((n) => {
        n.hidden = true;
        n.textContent = "";
      });
      this.pending++;
      this.writeVersion++;
      this.busy(true);
      const operation = this.tail.catch(() => {}).then(task);
      this.tail = operation
        .catch((error) => {
          this.error(error);
        })
        .finally(() => {
          this.pending--;
          this.busy(
            this.pending > 0 ||
              this.quantityTimers.size > 0 ||
              this.intents.size > 0,
          );
        });
      return operation;
    }
    busy(value) {
      document.querySelectorAll("av-cart").forEach((n) => {
        n.toggleAttribute("data-pending", value);
        n.setAttribute("aria-busy", String(value));
      });
      document.querySelectorAll("[data-checkout]").forEach((n) => {
        n.disabled = value;
      });
    }
    error(error) {
      this.lastError = error;
      document.querySelectorAll("[data-cart-error]").forEach((n) => {
        n.hidden = false;
        const message = document.createElement("span");
        message.textContent = error.message || A.strings.error;
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "av-link-button";
        retry.textContent = A.strings.retry;
        retry.addEventListener(
          "click",
          async () => {
            this.lastError = null;
            try {
              await this.refresh();
              if (
                this.noteDraft !== undefined &&
                this.noteDraft !== this.state.note
              )
                this.note(this.noteDraft);
            } catch (failure) {
              this.error(failure);
            }
          },
          { once: true },
        );
        n.replaceChildren(message, document.createTextNode(" "), retry);
      });
      A.announce(error.message || A.strings.error);
    }
    sectionPath() {
      const url = new URL(A.root, location.origin);
      const preview = new URL(location.href).searchParams.get(
        "preview_theme_id",
      );
      const theme = window.Shopify?.theme;
      const id =
        preview || (theme?.role && theme.role !== "main" ? theme.id : null);
      if (id) url.searchParams.set("preview_theme_id", id);
      return url.pathname + url.search;
    }
    sectionURL() {
      const url = new URL(this.sectionPath(), location.origin);
      url.searchParams.set("section_id", "cart-state");
      url.searchParams.set("_", Date.now());
      return url.pathname + url.search;
    }
    async refresh(confirmed = null) {
      const version = ++this.version,
        writeVersion = this.writeVersion;
      const bundled =
        confirmed?.sections?.["cart-state"] && Array.isArray(confirmed.items);
      const [response, cartResponse] = bundled
        ? [
            { text: async () => confirmed.sections["cart-state"] },
            { json: async () => confirmed },
          ]
        : await Promise.all([
            A.request(this.sectionURL()),
            A.request(`${A.root}cart.js`, {
              headers: { Accept: "application/json" },
              cache: "no-store",
            }),
          ]);
      const doc = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const source = doc.querySelector("av-cart");
      if (!source) throw new Error(A.strings.error);
      const state = await cartResponse.json();
      if (version !== this.version || writeVersion !== this.writeVersion)
        return;
      if (
        !state.item_count ||
        (this.state.token && state.token && this.state.token !== state.token)
      ) {
        this.codes = [];
        try {
          sessionStorage.removeItem("avenger-cart-codes");
        } catch (_) {}
      }
      this.state = state;
      const active = document.activeElement;
      const activeView = active?.closest("av-cart");
      const focusKey = active?.closest("[data-line-key]")?.dataset.lineKey;
      const focusType = active?.hasAttribute("data-cart-quantity")
        ? "quantity"
        : active?.hasAttribute("data-cart-note")
          ? "note"
          : null;
      const start = active?.selectionStart,
        end = active?.selectionEnd;
      document.querySelectorAll("av-cart").forEach((view) => {
        const open = new Set(
          [...view.querySelectorAll("details[open][data-cart-disclosure]")].map(
            (d) => d.dataset.cartDisclosure,
          ),
        );
        const shipping = view.querySelector("av-shipping");
        const clone = source.cloneNode(true);
        const context = view.dataset.context;
        if (context === "panel")
          clone
            .querySelectorAll("[data-cart-page-only]")
            .forEach((node) => node.remove());
        clone.querySelectorAll("[id]").forEach((n) => {
          n.id = n.id.replace("response", context);
        });
        clone.querySelectorAll("[for]").forEach((n) => {
          n.htmlFor = n.htmlFor.replace("response", context);
        });
        // Preserve the shipping controller/address; stale estimates are invalidated by cart events.
        const destinationShipping = clone.querySelector("av-shipping");
        if (shipping && destinationShipping)
          destinationShipping.replaceWith(shipping);
        const oldProgress =
          view
            .querySelector(".av-shipping-track")
            ?.style.getPropertyValue("--shipping-progress") || "0%";
        const track = clone.querySelector(".av-shipping-track");
        const nextProgress = track?.style.getPropertyValue(
          "--shipping-progress",
        );
        if (track) track.style.setProperty("--shipping-progress", oldProgress);
        view.dataset.savingsPrefix = source.dataset.savingsPrefix || "";
        view.replaceChildren(...clone.childNodes);
        if (track) {
          track.getBoundingClientRect();
          requestAnimationFrame(() => {
            if (!track.isConnected) return;
            if (this.intents.size) this.optimistic();
            else track.style.setProperty("--shipping-progress", nextProgress);
          });
        }
        view.querySelectorAll("details").forEach((d) => {
          d.open = open.has(d.dataset.cartDisclosure);
        });
        if (
          this.noteDraft !== undefined &&
          this.noteDraft !== state.note &&
          view.querySelector("[data-cart-note]")
        )
          view.querySelector("[data-cart-note]").value = this.noteDraft;
        if (view === activeView && focusType) {
          let input =
            focusType === "note"
              ? view.querySelector("[data-cart-note]")
              : [...view.querySelectorAll("[data-line-key]")]
                  .find((n) => n.dataset.lineKey === focusKey)
                  ?.querySelector("[data-cart-quantity]");
          if (!input && focusType === "quantity")
            input = view.querySelector("[data-cart-quantity]");
          input?.focus({ preventScroll: true });
          if (focusType === "note" && start !== null)
            input?.setSelectionRange(start, end);
        }
      });
      document.querySelectorAll("[data-cart-count]").forEach((n) => {
        n.textContent = state.item_count;
        n.hidden = !state.item_count && !n.hasAttribute("data-count-keep");
      });
      document
        .querySelectorAll("[data-cart-trigger]")
        .forEach((n) =>
          n.setAttribute(
            "aria-label",
            `${n.dataset.cartLabel || ""} (${state.item_count})`,
          ),
        );
      this.renderCodes();
      this.optimistic();
      if (this.lastError) this.error(this.lastError);
      this.busy(this.pending > 0 || this.quantityTimers.size > 0);
      this.dispatchEvent(new CustomEvent("change", { detail: state }));
    }
    change(key, quantity) {
      this.quantityTimers.get(key)?.cancel();
      // Coalesce clicks for this key before a network write. A key may change after discounts.
      const snapshot = this.state.items.find((i) => i.key === key);
      const signature = snapshot ? this.signature(snapshot) : null;
      for (const [oldKey, intent] of this.intents)
        if (oldKey !== key && signature && intent.signature === signature)
          this.intents.delete(oldKey);
      this.intents.set(key, { quantity, signature });
      const timer = {
        quantity,
        signature,
        cancel: () => clearTimeout(timer.id),
      };
      timer.id = setTimeout(() => {
        this.quantityTimers.delete(key);
        this.enqueue(async () => {
          const item =
            this.state.items.find((i) => i.key === key) ||
            this.state.items.find(
              (i) => timer.signature && this.signature(i) === timer.signature,
            );
          if (!item) {
            await this.refresh();
            throw new Error(A.strings.sync);
          }
          let confirmed;
          try {
            confirmed = await A.json(`${A.root}cart/change.js`, {
              id: item.key,
              quantity: timer.quantity,
              sections: "cart-state",
              sections_url: this.sectionPath(),
            });
          } catch (error) {
            if (this.intents.get(key)?.quantity === timer.quantity)
              this.intents.delete(key);
            this.rollback(key);
            await this.refresh().catch(() => {});
            throw error;
          }
          if (this.intents.get(key)?.quantity === timer.quantity)
            this.intents.delete(key);
          await this.refresh(confirmed);
        }).catch(() => {});
      }, 160);
      this.quantityTimers.set(key, timer);
      document.querySelectorAll("[data-line-key]").forEach((row) => {
        if (row.dataset.lineKey !== key) return;
        row.querySelector("[data-cart-quantity]").value = quantity;
        this.linePrice(row, Number(row.dataset.unitPrice) * quantity, quantity);
        row.classList.add("is-pending");
        if (quantity > 0 && row.avRemoval) {
          row.avRemoval.cancel();
          row.avRemoval = null;
          row.hidden = false;
          row.style.removeProperty("overflow");
        }
        if (quantity === 0 && !row.avRemoval) {
          row.style.overflow = "hidden";
          row.avRemoval = row.animate?.(
            [
              { opacity: 1, height: `${row.getBoundingClientRect().height}px` },
              {
                opacity: 0,
                height: "0px",
                paddingBlock: "0px",
                marginBlock: "0px",
              },
            ],
            { duration: A.duration(220), easing: "ease", fill: "forwards" },
          );
          row.avRemoval?.finished
            ?.then(() => {
              if (this.intents.get(key)?.quantity === 0) row.hidden = true;
            })
            .catch(() => {});
        }
      });
      this.optimistic();
      document.querySelectorAll("[data-checkout]").forEach((n) => {
        n.disabled = true;
      });
    }
    referenceUnit(item) {
      const row = [...document.querySelectorAll("[data-line-key]")].find(
        (n) => n.dataset.lineKey === item.key,
      );
      return (
        Number(row?.dataset.referenceUnit) ||
        item.original_price ||
        item.final_price ||
        0
      );
    }
    linePrice(row, total, quantity) {
      row.querySelector("[data-line-total]").textContent = A.money(total);
      const reference = Number(row.dataset.referenceUnit) * quantity;
      const compare = row.querySelector("[data-line-reference]");
      const savings = row.querySelector("[data-line-savings]");
      if (compare) {
        compare.textContent = A.money(reference);
        compare.hidden = reference <= total;
      }
      if (savings) {
        const prefix = row.closest("av-cart")?.dataset.savingsPrefix || "";
        savings.textContent = [prefix, A.money(Math.max(0, reference - total))]
          .filter(Boolean)
          .join(" ");
        savings.hidden = reference <= total;
      }
    }
    summaryPrices(total, reference) {
      document.querySelectorAll("av-cart").forEach((view) => {
        const compare = view.querySelector("[data-cart-reference]");
        const savings = view.querySelector("[data-cart-savings]");
        if (compare) {
          compare.textContent = A.money(Math.max(0, reference));
          compare.hidden = reference <= total;
        }
        if (savings) {
          savings.textContent = [
            view.dataset.savingsPrefix,
            A.money(Math.max(0, reference - total)),
          ]
            .filter(Boolean)
            .join(" ");
          savings.hidden = reference <= total;
        }
      });
    }
    optimistic() {
      if (!this.intents.size) return;
      let total = this.state.total_price || 0,
        count = this.state.item_count || 0;
      let reference = this.state.items.reduce(
        (sum, item) => sum + this.referenceUnit(item) * item.quantity,
        0,
      );
      for (const [key, intent] of this.intents) {
        const item =
          this.state.items.find((i) => i.key === key) ||
          this.state.items.find(
            (i) => intent.signature && this.signature(i) === intent.signature,
          );
        if (!item) continue;
        total += (intent.quantity - item.quantity) * item.final_price;
        count += intent.quantity - item.quantity;
        reference +=
          (intent.quantity - item.quantity) * this.referenceUnit(item);
        document.querySelectorAll("[data-line-key]").forEach((row) => {
          if (row.dataset.lineKey !== item.key) return;
          row.querySelector("[data-cart-quantity]").value = intent.quantity;
          this.linePrice(
            row,
            item.final_price * intent.quantity,
            intent.quantity,
          );
          row.classList.add("is-pending");
        });
      }
      total = Math.max(0, total);
      reference = Math.max(0, reference);
      document.querySelectorAll("[data-cart-total]").forEach((n) => {
        n.textContent = A.money(Math.max(0, total));
      });
      document.querySelectorAll("[data-cart-count]").forEach((n) => {
        n.textContent = Math.max(0, count);
        n.hidden = count <= 0 && !n.hasAttribute("data-count-keep");
      });
      this.summaryPrices(total, reference);
      this.progress(total);
      this.busy(true);
    }
    progress(total) {
      document.querySelectorAll(".av-shipping-track").forEach((track) => {
        const progress = track.querySelector("progress");
        if (!progress?.max) return;
        const percent = Math.max(
          0,
          Math.min(100, (total / progress.max) * 100),
        );
        track.style.setProperty("--shipping-progress", `${percent}%`);
        progress.value = Math.max(0, Math.min(progress.max, total));
        const container = track.closest(".av-shipping-progress");
        const message = container?.querySelector("[data-shipping-message]");
        container?.classList.toggle("is-reached", total >= progress.max);
        const remaining = Math.max(0, progress.max - total);
        const text =
          remaining > 0
            ? container?.dataset.remainingText
            : container?.dataset.reachedText;
        if (message && text)
          message.textContent = text.replaceAll("{amount}", A.money(remaining));
      });
    }
    rollback(key) {
      const item = this.state.items.find((item) => item.key === key);
      document.querySelectorAll("[data-line-key]").forEach((row) => {
        if (row.dataset.lineKey !== key) return;
        row.avRemoval?.cancel();
        row.avRemoval = null;
        row.hidden = false;
        row.style.removeProperty("overflow");
        row.classList.remove("is-pending");
        if (item) {
          row.querySelector("[data-cart-quantity]").value = item.quantity;
          this.linePrice(
            row,
            item.final_line_price ?? item.final_price * item.quantity,
            item.quantity,
          );
        }
      });
      document
        .querySelectorAll("[data-cart-total]")
        .forEach((n) => (n.textContent = A.money(this.state.total_price)));
      this.summaryPrices(
        this.state.total_price,
        this.state.items.reduce(
          (sum, item) => sum + this.referenceUnit(item) * item.quantity,
          0,
        ),
      );
      document.querySelectorAll("[data-cart-count]").forEach((n) => {
        n.textContent = this.state.item_count;
        n.hidden = !this.state.item_count && !n.hasAttribute("data-count-keep");
      });
      this.progress(this.state.total_price);
      this.optimistic();
    }
    note(value) {
      clearTimeout(this.noteTimer);
      this.noteDraft = value;
      document.querySelectorAll("[data-cart-note]").forEach((n) => {
        if (n !== document.activeElement) n.value = value;
      });
      this.noteTimer = setTimeout(() => {
        this.noteTimer = null;
        this.enqueue(async () => {
          const draft = this.noteDraft;
          await A.json(`${A.root}cart/update.js`, { note: draft });
          this.state.note = draft;
        }).catch(() => {});
      }, 450);
    }
    async flush() {
      for (const [key, timer] of [...this.quantityTimers]) {
        timer.cancel();
        this.quantityTimers.delete(key);
        this.enqueue(async () => {
          await A.json(`${A.root}cart/change.js`, {
            id:
              (
                this.state.items.find((i) => i.key === key) ||
                this.state.items.find(
                  (i) =>
                    timer.signature && this.signature(i) === timer.signature,
                )
              )?.key || key,
            quantity: timer.quantity,
          });
          await this.refresh();
        }).catch(() => {});
      }
      if (
        this.noteTimer ||
        (this.noteDraft !== undefined && this.noteDraft !== this.state.note)
      ) {
        clearTimeout(this.noteTimer);
        this.noteTimer = null;
        this.enqueue(() =>
          A.json(`${A.root}cart/update.js`, { note: this.noteDraft }),
        ).catch(() => {});
      }
      await this.tail;
      if (this.lastError) throw this.lastError;
      this.intents.clear();
      await this.refresh();
    }
    discount(code, remove = false) {
      return this.enqueue(async () => {
        const current = await (
          await A.request(`${A.root}cart.js`, { cache: "no-store" })
        ).json();
        let codes = current.discount_codes
          ? current.discount_codes.map((c) => c.code)
          : [...new Set([...this.codes, ...this.appliedCodes(current)])];
        codes = remove
          ? codes.filter((c) => c.toLowerCase() !== code.toLowerCase())
          : [
              ...new Set(
                [...codes, ...code.split(",").map((c) => c.trim())].filter(
                  Boolean,
                ),
              ),
            ].slice(0, 20);
        await A.json(`${A.root}cart/update.js`, { discount: codes.join(",") });
        this.codes = codes;
        try {
          sessionStorage.setItem("avenger-cart-codes", JSON.stringify(codes));
        } catch (_) {}
        await this.refresh();
        if (
          !remove &&
          this.state.discount_codes?.some(
            (c) =>
              c.code.toLowerCase() === code.trim().toLowerCase() &&
              !c.applicable,
          )
        )
          throw new Error(A.strings.discountError);
      });
    }
    signature(item) {
      return JSON.stringify([
        item.variant_id || item.id,
        Object.entries(item.properties || {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
        item.selling_plan_allocation?.selling_plan?.id || null,
      ]);
    }
    appliedCodes(state) {
      const applications = [
        ...(state.cart_level_discount_applications || []),
        ...state.items.flatMap((i) =>
          (i.line_level_discount_allocations || []).map(
            (d) => d.discount_application,
          ),
        ),
      ];
      return [
        ...new Set(
          applications
            .filter((d) => d?.type === "discount_code")
            .map((d) => d.title),
        ),
      ];
    }
    renderCodes() {
      const state = this.state;
      const codes =
        state.discount_codes ||
        [...new Set([...this.codes, ...this.appliedCodes(state)])].map(
          (code) => ({
            code,
            applicable: this.appliedCodes(state).some(
              (c) => c.toLowerCase() === code.toLowerCase(),
            ),
          }),
        );
      document.querySelectorAll("[data-discount-codes]").forEach((list) => {
        list.replaceChildren();
        for (const code of codes) {
          const li = document.createElement("li");
          const text = document.createElement("span");
          text.textContent =
            code.code +
            (code.applicable ? "" : ` — ${A.strings.discountPending}`);
          const b = document.createElement("button");
          b.type = "button";
          b.dataset.discountRemove = code.code;
          b.setAttribute("aria-label", `${A.strings.remove} ${code.code}`);
          b.textContent = "×";
          li.append(text, b);
          list.append(li);
        }
      });
    }
    add(form) {
      const body = new FormData(form);
      return this.enqueue(async () => {
        await A.request(`${A.root}cart/add.js`, {
          method: "POST",
          body,
          headers: { Accept: "application/json" },
        });
        await this.refresh();
        A.announce(A.strings.added);
      });
    }
  }
  const cart = (A.cart = new CartService());

  // Optional Buy now gate: Shopify cart writes finish before the app owns pickup + redirect.
  let checkoutOperation = null;
  const pickupFailure = (message) => {
    const error = new Error(message || A.strings.error);
    error.avPickupFailure = true;
    return error;
  };
  async function pickupApi() {
    for (let attempt = 0; attempt < 21; attempt++) {
      const api = window.InPostPickup;
      if (
        typeof api?.open === "function" &&
        typeof api?.isAvailable === "function"
      )
        return api;
      if (attempt < 20)
        await new Promise((resolve) => setTimeout(resolve, 250));
    }
    throw pickupFailure(A.strings.pickupMissing);
  }
  A.buyNowCheckout = (fallback) => {
    if (checkoutOperation) return checkoutOperation;
    checkoutOperation = (async () => {
      await cart.flush();
      if (!A.config.progusBuyNow) return fallback();
      const api = await pickupApi();
      let availability;
      try {
        availability = await api.isAvailable();
      } catch {
        throw pickupFailure(A.strings.pickupError);
      }
      if (availability?.available === false) {
        if (A.config.progusUnavailableContinue) return fallback();
        throw pickupFailure(A.strings.pickupUnavailable);
      }
      if (availability?.available !== true)
        throw pickupFailure(A.strings.pickupError);
      A.closePanels(true);
      try {
        // The app receives the checkout intent and controls save-before-redirect.
        // Never issue our own redirect on resolve, rejection or dismissal.
        await api.open({ checkout: true, locale: A.config.locale });
      } catch (error) {
        if (
          ["cancelled", "canceled"].includes(error?.code) ||
          error?.name === "AbortError"
        ) {
          A.announce(A.strings.pickupCancelled);
          return;
        }
        throw pickupFailure(A.strings.pickupError);
      }
    })().finally(() => {
      checkoutOperation = null;
    });
    return checkoutOperation;
  };

  class CartView extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener(
        "click",
        (e) => {
          const row = e.target.closest("[data-line-key]");
          const step = e.target.closest("[data-quantity-step]"),
            remove = e.target.closest("[data-cart-remove]");
          if (row && (step || remove)) {
            e.preventDefault();
            const input = row.querySelector("[data-cart-quantity]");
            cart.change(
              row.dataset.lineKey,
              remove
                ? 0
                : Math.max(
                    0,
                    Number(input.value) +
                      Number(step.dataset.quantityStep) *
                        Number(input.step || 1),
                  ),
            );
          }
          const code = e.target.closest("[data-discount-remove]");
          if (code)
            cart.discount(code.dataset.discountRemove, true).catch(() => {});
        },
        { signal },
      );
      this.addEventListener(
        "change",
        (e) => {
          if (e.target.matches("[data-cart-quantity]")) {
            const quantity = Number(e.target.value);
            if (Number.isInteger(quantity) && quantity >= 0)
              cart.change(
                e.target.closest("[data-line-key]").dataset.lineKey,
                quantity,
              );
            else e.target.reportValidity();
          }
        },
        { signal },
      );
      this.addEventListener(
        "input",
        (e) => {
          if (e.target.matches("[data-cart-note]")) cart.note(e.target.value);
        },
        { signal },
      );
      this.addEventListener(
        "submit",
        async (e) => {
          if (e.target.matches("[data-discount-form]")) {
            e.preventDefault();
            const input = e.target.elements.discount;
            await cart.discount(input.value).catch(() => {});
          }
          if (e.target.matches("[data-cart-form]")) {
            e.preventDefault();
            if (e.submitter?.name === "checkout") {
              try {
                await cart.flush();
                if (this.querySelector("[data-cart-error]:not([hidden])"))
                  return;
                const form = document.createElement("form");
                form.method = "post";
                form.action = A.config.cart;
                const checkout = document.createElement("input");
                checkout.type = "hidden";
                checkout.name = "checkout";
                checkout.value = "Checkout";
                form.append(checkout);
                document.body.append(form);
                form.submit();
              } catch (error) {
                cart.error(error);
              }
            } else await cart.flush().catch((error) => cart.error(error));
          }
        },
        { signal },
      );
    }
    disconnectedCallback() {
      this.abort?.abort();
    }
  }
  customElements.define("av-cart", CartView);
  class Shipping extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.country = this.querySelector("[data-country]");
      this.province = this.querySelector("[data-province]");
      if (this.country.getAttribute("default") && !this.initialized)
        this.country.value = this.country.getAttribute("default");
      if (!this.initialized) this.provinces();
      this.initialized = true;
      this.country.addEventListener("change", () => this.provinces(), {
        signal,
      });
      this.addEventListener("input", () => this.cancel(), { signal });
      cart.addEventListener("change", () => this.cancel(), { signal });
      this.querySelector("form").addEventListener(
        "submit",
        (e) => {
          e.preventDefault();
          this.estimate();
        },
        { signal },
      );
    }
    provinces() {
      const options = JSON.parse(
        this.country.selectedOptions[0]?.dataset.provinces || "[]",
      );
      this.province.replaceChildren(
        ...options.map(([value, text]) => new Option(text, value)),
      );
      this.querySelector("[data-province-wrap]").hidden = options.length === 0;
      this.cancel();
    }
    cancel() {
      this.requestAbort?.abort();
      this.querySelector("[data-rates]").textContent = "";
      this.querySelector("button").disabled = false;
    }
    async estimate() {
      this.cancel();
      this.requestAbort = new AbortController();
      const { signal } = this.requestAbort;
      const params = new URLSearchParams();
      const form = this.querySelector("form");
      for (const key of ["country", "province", "zip"])
        params.set(`shipping_address[${key}]`, form.elements[key].value);
      const output = this.querySelector("[data-rates]");
      output.textContent = A.strings.loading;
      form.querySelector("button").disabled = true;
      try {
        await A.request(`${A.root}cart/prepare_shipping_rates.json?${params}`, {
          method: "POST",
          signal,
        });
        let rates = null;
        for (let i = 0; i < 10; i++) {
          const data = await (
            await A.request(
              `${A.root}cart/async_shipping_rates.json?${params}`,
              { signal },
            )
          ).json();
          if (data !== null) {
            rates = data.shipping_rates || [];
            break;
          }
          await new Promise((resolve, reject) => {
            const id = setTimeout(resolve, Math.min(500 + i * 200, 1800));
            signal.addEventListener(
              "abort",
              () => {
                clearTimeout(id);
                reject(new DOMException("Aborted", "AbortError"));
              },
              { once: true },
            );
          });
        }
        if (rates === null) throw new Error(A.strings.shippingError);
        output.replaceChildren();
        if (!rates.length) output.textContent = A.strings.noRates;
        for (const rate of rates) {
          const p = document.createElement("p");
          p.textContent = `${rate.name}: ${A.money(Math.round(Number(rate.price) * 100))}`;
          output.append(p);
        }
      } catch (error) {
        if (error.name !== "AbortError")
          output.textContent = error.message || A.strings.shippingError;
      } finally {
        if (!signal.aborted) form.querySelector("button").disabled = false;
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.requestAbort?.abort();
    }
  }
  customElements.define("av-shipping", Shipping);
})();
